<template>
  <BaseRightDrawer v-model:drawer="drawer" @right-drawer-close="drawer = false">
    <template #loading>
      <v-progress-linear
        class="position-absolute"
        style="top: 0"
        indeterminate
        :active="isPending"
      />
    </template>

    <template #error>
      <ErrorAlert v-if="error != null" :error="error" class="mb-0 mb-4 rounded" />
    </template>

    <template #header-before-close v-if="customer">
      <CustomerDetailOptionsButton
        class="me-4"
        :customer="customer"
        @customer-edit-pressed="(customer: ICustomer) => emit('customer-edit-pressed', customer)"
        @customer-delete-pressed="
          (customer: ICustomer) => emit('customer-delete-pressed', customer)
        "
        @customer-change-password-pressed="
          (customer: ICustomer) => emit('customer-change-password-pressed', customer)
        "
        @customer-change-sites-pressed="openCustomerSitesDialog()"
        @customer-set-active-pressed="
          (customer: ICustomer) => emit('customer-set-active-pressed', customer)
        "
      />
    </template>

    <div class="d-flex flex-column flex-grow-1" v-if="customer">
      <v-list-item lines="three" class="position-relative rounded-lg mx-6 mb-2 px-0 pb-1 pt-0">
        <v-list-item-action>
          <v-avatar
            variant="flat"
            color="secondary"
            size="74"
            class="mb-6 px-3 text-h4"
            style="border-radius: 5px"
          >
            <span class="text-white font-weight-medium">{{ customer.getInitials() }}</span>
          </v-avatar>
        </v-list-item-action>
        <v-list-item-title class="text-high-emphasis text-h4 font-weight-bold pt-1">
          {{ customer.getFullName() }}
          <div
            v-if="customer.profile!.title"
            class="text-h6 font-weight-regular text-black mb-n2 pt-1"
            style="padding-inline-start: 2px"
          >
            {{ customer.profile!.title }}
          </div>
          <div class="pt-2 pb-6">
            <CustomerActiveStatusChip :is-active="customer.is_active!" />
          </div>
        </v-list-item-title>
        <v-list-subheader
          class="text-medium-emphasis text-body-2 font-weight-regular pb-3"
          style="min-height: 22px"
        >
          Customer Information
        </v-list-subheader>
        <v-list-item-subtitle
          class="detail-item-subtitle text-high-emphasis pb-4"
          style="padding-inline-start: 2px"
        >
          <div class="d-flex flex-column">
            <div
              class="text-medium-emphasis text-caption font-weight-medium"
              style="line-height: 0.9rem"
            >
              Email / Username
            </div>
            <a
              class="text-body-2 font-weight-medium text-link text-black"
              :href="`mailto:${customer.email}`"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ customer.email }}
            </a>
          </div>
        </v-list-item-subtitle>

        <v-list-item-subtitle
          class="detail-item-subtitle text-high-emphasis pb-4"
          style="padding-inline-start: 2px"
        >
        </v-list-item-subtitle>

        <v-list-item-subtitle
          class="detail-item-subtitle text-high-emphasis pb-4"
          style="padding-inline-start: 2px"
        >
          <div class="d-flex flex-column">
            <div
              class="d-flex flex-row align-center text-medium-emphasis text-caption font-weight-medium"
              style="line-height: 0.9rem"
            >
              Sites
              <CustomerDetailSitesFormDialog
                :customerId="customerId"
                v-model:dialog="customerSitesDialog"
              />
            </div>
            <div class="text-body-2 font-weight-medium text-black mt-1">
              <template v-if="customer.profile.site_assignments.length > 0">
                <template
                  v-for="(assignment, assignmentIndex) in customer.profile.site_assignments"
                  :key="assignmentIndex"
                >
                  <v-card variant="outlined" class="rounded-lg border mb-2" tag="ul">
                    <div class="w-full py-2 px-3">
                      <div class="text-subtitle-2 font-weight-500 text-link text-black">
                        {{ assignment.client?.name ?? '-' }}
                      </div>
                      <div
                        class="text-subtitle-2 d-block address mt-1 text-wrap"
                        v-if="assignment.site.address"
                      >
                        <div class="d-block address__field">
                          {{ assignment.site.address.address1 }}
                        </div>
                        <div class="d-block address__field">
                          {{ assignment.site.address.address2 }}
                        </div>

                        <div class="d-block address__field">
                          <span class="wv-text wv-text--body">
                            {{ assignment.site.address.city }},
                            {{ assignment.site.address.region }}
                          </span>
                        </div>
                        <div class="d-block address__field">
                          <span class="wv-text wv-text--body">
                            <span>
                              {{ assignment.site.address.country }},
                              {{ assignment.site.address.postal_code }}
                            </span>
                          </span>
                        </div>
                      </div>
                    </div>
                  </v-card>
                </template>
              </template>
              <span v-else class="text-high-emphasis text-body-2 font-weight-medium">None</span>
            </div>
          </div>
        </v-list-item-subtitle>

        <v-list-item-subtitle
          class="detail-item-subtitle text-high-emphasis pb-4"
          style="padding-inline-start: 2px"
        >
          <div class="d-flex flex-column">
            <div
              class="text-medium-emphasis text-caption font-weight-medium"
              style="line-height: 0.9rem"
            >
              Last Login
            </div>
            <div class="text-body-2 font-weight-medium text-black">
              {{
                customer.last_login
                  ? dateAdapter.formatByString(customer.last_login, 'PPP pp')
                  : 'None'
              }}
            </div>
          </div>
        </v-list-item-subtitle>
      </v-list-item>
    </div>
  </BaseRightDrawer>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDate } from 'vuetify'

import type DateFnsUtils from '@date-io/date-fns/build/date-fns-utils'
import type { ICustomer } from '@/models/customer'

import { useFetchCustomer } from '@/composables/customer'

import BaseRightDrawer from '@/components/base/BaseRightDrawer.vue'
import CustomerActiveStatusChip from './CustomerActiveStatusChip.vue'
import CustomerDetailOptionsButton from './CustomerDetailOptionsButton.vue'
import ErrorAlert from '../common/ErrorAlert.vue'
import CustomerDetailSitesFormDialog from './CustomerDetailSitesFormDialog.vue'

interface Props {
  customerId: number
}
const props = defineProps<Props>()

interface Emit {
  (e: 'customer-edit-pressed', customer: ICustomer): void
  (e: 'customer-delete-pressed', customer: ICustomer): void
  (e: 'customer-change-password-pressed', customer: ICustomer): void
  (e: 'customer-set-active-pressed', customer: ICustomer): void
}
const emit = defineEmits<Emit>()

const drawer = defineModel<boolean>('drawer')

const dateAdapter = useDate() as DateFnsUtils

const customerId = computed(() => props.customerId!)

const { customer, isPending, error } = useFetchCustomer(customerId)

const customerSitesDialog = ref(false)

function openCustomerSitesDialog() {
  customerSitesDialog.value = true
}
</script>

<style lang="scss">
.detail-item-subtitle {
  opacity: 1;
}
</style>
